import { Position } from 'geojson';
import { atom, selector, selectorFamily } from 'recoil';
import { EVENT_ENGINE_OFF } from '../constants/gpt';
import { Event, EventAlarmType, GptEventTimeLine, TimeLineType } from '../types/gpt';

export const eventDetailState = atom<Event | undefined>({
  key: 'app.gpt.events.detail',
  default: undefined,
});

export const eventAlarmTypesState = atom<EventAlarmType[] | undefined>({
  key: 'app.gpt.events.alarm.types',
  default: undefined,
});

export const loadedEventAlarmTypesState = atom<boolean>({
  key: 'app.gpt.events.alarm.types.loaded',
  default: false,
});

export const mapGptEventAlarmsGeoJsonState = selector<GeoJSON.Feature[]>({
  key: 'app.map.gpt.alarms.geojson',
  get: ({ get }) => {
    const event = get(eventDetailState);

    if (!event) return [];

    const { alarms } = event;

    if (!alarms) return [];

    const alarmMarkers = alarms.map((alarm) => ({
      alarm,
      geometry: {
        type: 'Point',
        coordinates: [alarm.gpsLng ?? alarm.gpsLng, alarm.gpsLat ?? alarm.gpsLat],
      },
    }));

    return alarmMarkers.map(
      (alarmMarker) =>
        ({
          type: 'Feature',
          properties: { cluster: false, alarm: alarmMarker.alarm },
          geometry: alarmMarker.geometry,
        }) as GeoJSON.Feature,
    );
  },
});

export const timelineEventsState = atom<GptEventTimeLine[] | undefined>({
  key: 'app.gpt.timeline',
  default: undefined,
});

export const eventHoveredState = atom<string | undefined>({
  key: 'app.gpt.timeline.hover',
  default: undefined,
});

export const timelineEventIsHoverState = selectorFamily({
  key: 'app.gpt.timeline.event.hovered',
  get:
    (uuid) =>
    ({ get }) => {
      const eventHovered = get(eventHoveredState);
      return uuid === eventHovered;
    },
});

export const eventFilterState = atom<TimeLineType | undefined>({
  key: 'app.gpt.timeline.filter',
  default: undefined,
});

export const filterTimelineEventsState = selector<GptEventTimeLine[] | undefined>({
  key: 'app.gpt.timeline.events',
  get: ({ get }) => {
    const timeLineEvents = get(timelineEventsState);
    if (!timeLineEvents) return;

    const eventTypeSelected = get(eventFilterState);
    let filteredTimeLineEvents = [...timeLineEvents];
    if (eventTypeSelected) {
      filteredTimeLineEvents = timeLineEvents.filter(
        (event) =>
          event.timelineType === eventTypeSelected ||
          event.events?.some((nestedEvent) => nestedEvent.timelineType === eventTypeSelected),
      );
    }

    return filteredTimeLineEvents;
  },
});

export const mapGptEventEngineOffGeoJsonState = selector<GeoJSON.Feature[]>({
  key: 'app.map.gpt.engineOff.geojson',
  get: ({ get }) => {
    const timeline = get(timelineEventsState);

    if (!timeline) return [];

    const engineOffEvents = timeline.filter((e) => e.timelineType === EVENT_ENGINE_OFF);

    if (engineOffEvents.length === 0) return [];

    const alarmMarkers = engineOffEvents.map((alarm) => ({
      alarm,
      geometry: alarm.startPoint,
    }));

    return alarmMarkers.map(
      (alarmMarker) =>
        ({
          type: 'Feature',
          properties: { cluster: false, alarm: alarmMarker.alarm },
          geometry: alarmMarker.geometry,
        }) as GeoJSON.Feature,
    );
  },
});

export const timelineHoverEventsState = selector<GptEventTimeLine[] | undefined>({
  key: 'app.gpt.timeline.hovered',
  get: ({ get }) => {
    const timeLineEvents = get(timelineEventsState);
    if (!timeLineEvents) return;

    const eventHovered = get(eventHoveredState);
    const event = timeLineEvents.find((e) => e.uuid === eventHovered);

    return event ? [event] : timeLineEvents;
  },
});

export const timelineEventPathsState = selector({
  key: 'app.gpt.timeline.event.paths',
  get: ({ get }) => {
    const eventTimeline = get(timelineHoverEventsState);
    if (!eventTimeline?.length) return;
    const eventPaths = eventTimeline.filter((e) => e.lineStringCoordinates);

    if (eventPaths.length > 0) {
      return eventPaths;
    }

    return undefined;
  },
});

export const timelineEventPathLngLatsState = selector<Position[] | undefined>({
  key: 'app.gpt.timeline.event.paths.lnglats',
  get: ({ get }) => {
    const paths = get(timelineEventPathsState);
    if (!paths) return;

    const validPaths = paths.filter(
      (path) => !!path.lineStringCoordinates && Array.isArray(path.lineStringCoordinates),
    );

    const lngLats = validPaths.flatMap((path) => {
      if (path.lineStringCoordinates!.length === 1) {
        return [path.lineStringCoordinates![0]];
      }
      return path.lineStringCoordinates!;
    });

    return lngLats;
  },
});
